import React, { useEffect, useRef, useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Skeleton } from 'primereact/skeleton';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { UserService } from '../../../service/UserService';
import { errorService } from '../../../services/ErrorService';
import { showErrorToast, showSuccessToast } from '../../../utils/ToastUtils';
import { setUserActiveStatus } from '../userSlice';
import moment from 'moment';

const userService = new UserService();

const userDisplayName = (user) => {
    if (!user) return '';
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
    return name || user.email;
};

// O campo guarda só o @handle (ex: "dr.addlersteve") — mas cobre também caso
// já venha com "@" na frente ou com a URL completa salva.
const instagramUrl = (handle) => {
    if (!handle) return null;
    if (/^https?:\/\//i.test(handle)) return handle;
    return `https://www.instagram.com/${handle.replace(/^@/, '')}`;
};

const formatDate = (value) => {
    if (!value) return '—';
    return moment(value).locale('pt-br').format('DD/MM/YYYY HH:mm');
};

// Mesmo padrão de badge do exemplo em UI Components > Table (product-badge
// status-instock/outofstock/lowstock).
const subscriptionStatus = (row) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = new Date(row.end_on);
    if (endDate < today) return 'expirado';
    return row.is_active ? 'ativo' : 'inativo';
};

const subscriptionStatusBadgeClass = {
    ativo: 'status-instock',
    inativo: 'status-lowstock',
    expirado: 'status-outofstock',
};

const subscriptionStatusLabel = {
    ativo: 'Ativo',
    inativo: 'Inativo',
    expirado: 'Expirado',
};

const subscriptionStatusBodyTemplate = (rowData) => {
    const status = subscriptionStatus(rowData);
    return <span className={`product-badge ${subscriptionStatusBadgeClass[status]}`}>{subscriptionStatusLabel[status]}</span>;
};

const deviceStatusBodyTemplate = (rowData) => {
    const active = !!rowData.active;
    return <span className={`product-badge ${active ? 'status-instock' : 'status-outofstock'}`}>{active ? 'Ativo' : 'Inativo'}</span>;
};

// Mesmo conceito usado na coluna "Status" do grid de usuários: ativo é sobre
// ter assinatura com is_active, não sobre isHidden.
const UserActiveBadge = ({ status }) => {
    if (!status) return null;
    const active = status === 'ativo';
    return <span className={`product-badge ${active ? 'status-instock' : 'status-outofstock'}`}>{active ? 'Ativo' : 'Inativo'}</span>;
};

const dateBodyTemplate = (field) => (rowData) => formatDate(rowData[field]);

// createdAt aqui vem da tabela de junção (anexado pelo backend depois da
// busca) — não é uma coluna do model em si, então não dá pra ordenar via
// filter na API. Ordena no cliente mesmo, mais recente primeiro.
const sortByCreatedAtDesc = (list) => {
    return [...(list || [])].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
};

const InfoField = ({ label, value }) => (
    <div className="col-12 md:col-6 lg:col-4">
        <span className="block text-500 font-medium mb-1">{label}</span>
        <span className="block text-900">{value || '—'}</span>
    </div>
);

const skeletonRows = () => Array.from({ length: 5 }, (_, i) => ({ id: `skeleton-${i}` }));

// O Accordion do PrimeReact não faz lazy-load de conteúdo (sempre monta os
// filhos, só escode via CSS) — por isso cada painel só busca dados quando
// `active` fica true (painel aberto), e some da tela via `if (!active)`
// enquanto isso não acontece.
const ServicesTab = ({ userId, active }) => {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (!active || loaded) return;
        setLoading(true);
        setError(null);
        userService.getMedicalServices(userId)
            .then(result => {
                setServices(sortByCreatedAtDesc(result));
                setLoaded(true);
            })
            .catch(() => setError('Não foi possível carregar os serviços.'))
            .finally(() => setLoading(false));
    }, [active, loaded, userId]);

    if (!active) return null;
    if (error) return <p>{error}</p>;

    return (
        <DataTable value={loading ? skeletonRows() : services} paginator rows={5} rowsPerPageOptions={[5, 10, 25, 50]} responsiveLayout="scroll" emptyMessage="Nenhum serviço encontrado." sortField="createdAt" sortOrder={-1}>
            <Column field="name" header="Serviço" sortable={!loading} body={loading ? () => <Skeleton /> : undefined}></Column>
            <Column header="Cidade/Estado" body={loading ? () => <Skeleton /> : (row) => [row.city, row.state].filter(Boolean).join(' — ') || '—'}></Column>
            <Column field="createdAt" header="Data" sortable={!loading} body={loading ? () => <Skeleton /> : dateBodyTemplate('createdAt')} style={{ width: '12rem' }}></Column>
        </DataTable>
    );
};

const ResidencesTab = ({ userId, active }) => {
    const [residences, setResidences] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (!active || loaded) return;
        setLoading(true);
        setError(null);
        userService.getResidences(userId)
            .then(result => {
                setResidences(sortByCreatedAtDesc(result));
                setLoaded(true);
            })
            .catch(() => setError('Não foi possível carregar as residências.'))
            .finally(() => setLoading(false));
    }, [active, loaded, userId]);

    if (!active) return null;
    if (error) return <p>{error}</p>;

    return (
        <DataTable value={loading ? skeletonRows() : residences} paginator rows={5} rowsPerPageOptions={[5, 10, 25, 50]} responsiveLayout="scroll" emptyMessage="Nenhuma residência encontrada." sortField="createdAt" sortOrder={-1}>
            <Column field="description" header="Residência" sortable={!loading} body={loading ? () => <Skeleton /> : undefined}></Column>
            <Column field="createdAt" header="Data" sortable={!loading} body={loading ? () => <Skeleton /> : dateBodyTemplate('createdAt')} style={{ width: '12rem' }}></Column>
        </DataTable>
    );
};

const SubscriptionsTab = ({ userEmail, active }) => {
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (!active || loaded || !userEmail) return;
        setLoading(true);
        setError(null);
        userService.getSubscriptions(userEmail)
            .then(result => {
                setSubscriptions(result || []);
                setLoaded(true);
            })
            .catch(() => setError('Não foi possível carregar as assinaturas.'))
            .finally(() => setLoading(false));
    }, [active, loaded, userEmail]);

    if (!active) return null;
    if (error) return <p>{error}</p>;

    return (
        <DataTable value={loading ? skeletonRows() : subscriptions} paginator rows={5} rowsPerPageOptions={[5, 10, 25, 50]} responsiveLayout="scroll" emptyMessage="Nenhuma assinatura encontrada.">
            <Column field="start_on" header="Início" sortable={!loading} body={loading ? () => <Skeleton /> : dateBodyTemplate('start_on')}></Column>
            <Column field="end_on" header="Fim" sortable={!loading} body={loading ? () => <Skeleton /> : dateBodyTemplate('end_on')}></Column>
            <Column field="product_name" header="Produto" sortable={!loading} body={loading ? () => <Skeleton /> : undefined}></Column>
            <Column field="product_code" header="Código" sortable={!loading} body={loading ? () => <Skeleton /> : undefined}></Column>
            <Column field="residence" header="Residência" sortable={!loading} body={loading ? () => <Skeleton /> : (row) => row.residence?.split(' - ')[0].trim() || '—'}></Column>
            <Column header="Status" body={loading ? () => <Skeleton /> : subscriptionStatusBodyTemplate} bodyClassName="text-center" style={{ width: '10rem' }}></Column>
            <Column field="cancelled_on" header="Cancelada em" sortable={!loading} body={loading ? () => <Skeleton /> : dateBodyTemplate('cancelled_on')}></Column>
            <Column field="cancel_reason" header="Motivo do cancelamento" body={loading ? () => <Skeleton /> : (row) => row.cancel_reason || '—'}></Column>
        </DataTable>
    );
};

const DevicesTab = ({ userId, active }) => {
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (!active || loaded) return;
        setLoading(true);
        setError(null);
        userService.getDevices(userId)
            .then(result => {
                setDevices(result || []);
                setLoaded(true);
            })
            .catch(() => setError('Não foi possível carregar os dispositivos.'))
            .finally(() => setLoading(false));
    }, [active, loaded, userId]);

    if (!active) return null;
    if (error) return <p>{error}</p>;

    return (
        <DataTable value={loading ? skeletonRows() : devices} paginator rows={5} rowsPerPageOptions={[5, 10, 25, 50]} responsiveLayout="scroll" emptyMessage="Nenhum dispositivo encontrado.">
            <Column field="identifier" header="Identificador" sortable={!loading} body={loading ? () => <Skeleton /> : undefined}></Column>
            <Column field="createdAt" header="Registrado em" sortable={!loading} body={loading ? () => <Skeleton /> : dateBodyTemplate('createdAt')}></Column>
            <Column header="Status" body={loading ? () => <Skeleton /> : deviceStatusBodyTemplate} bodyClassName="text-center" style={{ width: '10rem' }}></Column>
        </DataTable>
    );
};

export const UserDetailsPage = () => {
    const { id } = useParams();
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    // A linha da grid de Usuários já tem os dados básicos (vem via
    // history.push(path, { user: rowData })) — usado como valor inicial pra
    // não esperar nenhuma busca. Ainda busca /users/{id} em segundo plano
    // (sem bloquear a tela), pra funcionar também em reload direto da URL,
    // onde esse state não existe.
    const [user, setUser] = useState(location.state?.user ?? null);
    const [activeIndex, setActiveIndex] = useState(null);
    // O grid já manda esse status pré-calculado (location.state.user.activeStatus)
    // — usado como valor inicial. Ainda assim busca as assinaturas de novo aqui
    // (independente do accordion "Assinaturas", que só carrega quando aberto)
    // pra ter um valor confiável mesmo em reload direto da URL.
    const [activeStatus, setActiveStatus] = useState(location.state?.user?.activeStatus ?? null);
    const [displayStatusDialog, setDisplayStatusDialog] = useState(false);
    // Motivo do cancelamento, só usado ao desativar — obrigatório aqui na UI,
    // mas o backend ainda aceita vazio e cai no default "Desativado pelo
    // Admin" (chamadas fora dessa tela, ex. API).
    const [statusReason, setStatusReason] = useState('');
    const [displayResetDevicesDialog, setDisplayResetDevicesDialog] = useState(false);
    // Compartilhado pelos 2 diálogos de confirmação abaixo — só um fica
    // aberto por vez, então um estado só já basta pro loading do botão "Sim".
    const [confirming, setConfirming] = useState(false);
    // Bumped depois de ativar/desativar ou resetar dispositivos, pra forçar o
    // AccordionTab correspondente a remontar e buscar de novo se o usuário já
    // tinha aberto ele antes (senão ficaria mostrando dado desatualizado).
    const [subscriptionsRefreshKey, setSubscriptionsRefreshKey] = useState(0);
    const [devicesRefreshKey, setDevicesRefreshKey] = useState(0);
    const toast = useRef(null);

    useEffect(() => {
        setUser(location.state?.user ?? null);
        userService.getUser(id)
            .then(fullUser => setUser(fullUser))
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (!user?.email) return;
        userService.getSubscriptions(user.email)
            .then(subs => setActiveStatus((subs || []).some(s => s.is_active) ? 'ativo' : 'inativo'))
            .catch(() => {});
    }, [user?.email]);

    // Mesma lógica de "reativar" já usada no lote da grid — bulk-status com
    // um único id serve perfeitamente pra um usuário só.
    const confirmToggleStatus = async () => {
        setConfirming(true);
        const nextActive = activeStatus !== 'ativo';
        try {
            await errorService.retryOperation(async () => {
                const result = await userService.bulkSetSubscriptionStatus([Number(id)], nextActive, statusReason);
                if (nextActive && result?.skipped?.length > 0) {
                    showSuccessToast(toast, 'Aviso', 'Este usuário não tem assinatura válida para reativar.');
                } else {
                    const newStatus = nextActive ? 'ativo' : 'inativo';
                    setActiveStatus(newStatus);
                    setSubscriptionsRefreshKey(k => k + 1);
                    dispatch(setUserActiveStatus({ userId: Number(id), activeStatus: newStatus }));
                    showSuccessToast(toast, 'Sucesso', `Usuário ${nextActive ? 'ativado' : 'desativado'} com sucesso.`);
                }
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao atualizar assinatura', error);
        }
        setConfirming(false);
        setDisplayStatusDialog(false);
    };

    // Desativa todos os dispositivos do usuário — não bloqueia acesso, só
    // limpa o "dispositivo ativo" atual; o próximo app aberto se registra
    // sozinho como o novo ativo (mesmo comportamento de sempre).
    const confirmResetDevices = async () => {
        setConfirming(true);
        try {
            await errorService.retryOperation(async () => {
                await userService.resetDevices(id);
                setDevicesRefreshKey(k => k + 1);
                showSuccessToast(toast, 'Sucesso', 'Dispositivos resetados com sucesso.');
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao resetar dispositivos', error);
        }
        setConfirming(false);
        setDisplayResetDevicesDialog(false);
    };

    // Motivo obrigatório só na UI (backend aceita vazio e cai no default
    // "Desativado pelo Admin") — força o admin a preencher antes de confirmar.
    const statusConfirmDisabled = activeStatus === 'ativo' && !statusReason.trim();

    const statusDialogFooter = (
        <>
            <Button type="button" label="Não" icon="pi pi-times" onClick={() => setDisplayStatusDialog(false)} className="p-button-text" disabled={confirming} />
            <Button type="button" label="Sim" icon="pi pi-check" onClick={confirmToggleStatus} className="p-button-text" loading={confirming} disabled={statusConfirmDisabled} autoFocus />
        </>
    );

    const resetDevicesDialogFooter = (
        <>
            <Button type="button" label="Não" icon="pi pi-times" onClick={() => setDisplayResetDevicesDialog(false)} className="p-button-text" disabled={confirming} />
            <Button type="button" label="Sim" icon="pi pi-check" onClick={confirmResetDevices} className="p-button-text" loading={confirming} autoFocus />
        </>
    );

    return (
        <div className="grid p-fluid">
            <div className="col-12">
                <div className="card">
                    <Toast ref={toast} />
                    <div className="flex justify-content-between align-items-center mb-4">
                        <div className="flex align-items-center gap-3">
                            <Button
                                icon="pi pi-arrow-left"
                                rounded
                                text
                                onClick={() => history.goBack()}
                            />
                            <h5 className="m-0">Detalhes do usuário</h5>
                        </div>
                        <div className="flex gap-2">
                            {!user?.isAdmin && (
                                <Button
                                    label={activeStatus === 'ativo' ? 'Desativar' : 'Ativar'}
                                    icon={activeStatus === 'ativo' ? 'pi pi-times' : 'pi pi-check'}
                                    className={activeStatus === 'ativo' ? 'p-button-danger' : 'p-button-success'}
                                    onClick={() => { setStatusReason(''); setDisplayStatusDialog(true); }}
                                    disabled={!activeStatus}
                                />
                            )}
                            <Button
                                label="Resetar dispositivo"
                                icon="pi pi-mobile"
                                className="p-button-outlined"
                                onClick={() => setDisplayResetDevicesDialog(true)}
                            />
                        </div>
                    </div>

                    <div className="grid mb-4">
                        <InfoField label="Nome" value={userDisplayName(user)} />
                        <InfoField label="Email" value={user?.email} />
                        <InfoField label="Celular" value={user?.phone} />
                        <InfoField label="Instagram" value={user?.instagram && (
                            <a href={instagramUrl(user.instagram)} target="_blank" rel="noopener noreferrer">{user.instagram}</a>
                        )} />
                        <InfoField label="Data de Cadastro" value={formatDate(user?.createdAt)} />
                        {!user?.isAdmin && <InfoField label="Status" value={<UserActiveBadge status={activeStatus} />} />}
                    </div>

                    <Accordion activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
                        <AccordionTab header="Serviços">
                            <ServicesTab userId={id} active={activeIndex === 0} />
                        </AccordionTab>
                        <AccordionTab header="Residências">
                            <ResidencesTab userId={id} active={activeIndex === 1} />
                        </AccordionTab>
                        <AccordionTab header="Assinaturas">
                            <SubscriptionsTab key={subscriptionsRefreshKey} userEmail={user?.email} active={activeIndex === 2} />
                        </AccordionTab>
                        <AccordionTab header="Dispositivos">
                            <DevicesTab key={devicesRefreshKey} userId={id} active={activeIndex === 3} />
                        </AccordionTab>
                    </Accordion>

                    <Dialog header={activeStatus === 'ativo' ? 'Desativar assinatura' : 'Ativar assinatura'} visible={displayStatusDialog} onHide={() => setDisplayStatusDialog(false)} style={{ width: '400px' }} modal footer={statusDialogFooter}>
                        <div className="flex align-items-center justify-content-center">
                            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                            <span>Deseja {activeStatus === 'ativo' ? 'desativar' : 'ativar'} a assinatura deste usuário?</span>
                        </div>
                        {activeStatus === 'ativo' && (
                            <div className="flex flex-column gap-1 mt-3">
                                <label htmlFor="status-reason" className="text-xs text-color-secondary">Motivo *</label>
                                <InputText
                                    id="status-reason"
                                    value={statusReason}
                                    onChange={(e) => setStatusReason(e.target.value)}
                                    placeholder="Descreva o motivo da desativação"
                                    disabled={confirming}
                                    className="w-full"
                                />
                            </div>
                        )}
                    </Dialog>
                    <Dialog header="Resetar dispositivo" visible={displayResetDevicesDialog} onHide={() => setDisplayResetDevicesDialog(false)} style={{ width: '400px' }} modal footer={resetDevicesDialogFooter}>
                        <div className="flex align-items-center justify-content-center">
                            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                            <span>Isso não bloqueia o acesso do usuário — só limpa o dispositivo ativo atual. Na próxima vez que abrir o app, o dispositivo em uso se torna o novo ativo automaticamente. Deseja continuar?</span>
                        </div>
                    </Dialog>
                </div>
            </div>
        </div>
    );
};

export default UserDetailsPage;
